import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Papa from 'papaparse';
import toast from 'react-hot-toast';
import { uploadSolarData, getNewDeviceList } from '../../Database/Action/DashboardAction';

const UploadDataModal = ({ show, onClose }) => {
  const dispatch = useDispatch();
  const [selectedUID, setSelectedUID] = useState('');
  const [uploading, setUploading] = useState(false);
  const [filenameUID, setFilenameUID] = useState('');
  const [filename, setFilename] = useState('');

  const deviceList = useSelector(state => state.DashboardReducer.newDeviceList || []);

  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  useEffect(() => {
    dispatch(getNewDeviceList());
  }, [dispatch]);

  // ── Search: UID + Location + Name + Village + Block + District ──────────────
  const query = searchQuery.trim().toUpperCase();

  const filteredDevices = query === ''
    ? []
    : deviceList.filter(device =>
        String(device?.UID || '').toUpperCase().includes(query) ||
        String(device?.Location || '').toUpperCase().includes(query) ||
        String(device?.NameOfBeneficiary || '').toUpperCase().includes(query) ||
        String(device?.VillageName || '').toUpperCase().includes(query) ||
        String(device?.Block || '').toUpperCase().includes(query) ||
        String(device?.District || '').toUpperCase().includes(query)
      );

  const extractUIDFromFilename = (filename) => {
    return filename.replace(/\.csv$/i, '').trim();
  };

  const normalizeUID = (uid) => {
    return String(uid || '').trim().toUpperCase();
  };

  const safeParseFloat = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  const findDeviceByUID = (uidFromFilename) => {
    const normalizedTarget = normalizeUID(uidFromFilename);
    return deviceList.find(device => normalizeUID(device?.UID) === normalizedTarget) || null;
  };

  const isUIDRegistered = (uid) => findDeviceByUID(uid) !== null;

  const peekFirstLine = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const text = reader.result || '';
        resolve(text.split(/\r?\n/)[0] || '');
      };
      reader.onerror = reject;
      reader.readAsText(file.slice(0, 200));
    });
  };

  const isRawFirmwareFormat = (firstLine) => {
    const trimmed = firstLine.trim();
    return trimmed.startsWith('{') && /UID\s*:/.test(trimmed);
  };

  const unixToISO = (unixSeconds) => {
    const ms = parseFloat(unixSeconds) * 1000;
    if (isNaN(ms)) return new Date().toISOString();
    return new Date(ms).toISOString();
  };

  const parseRawFirmwareCSV = async (file) => {
    const text = await file.text();
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
    const dataLines = lines.slice(1);
    const parsedRows = [];
    const skippedRows = [];

    dataLines.forEach((line, idx) => {
      const parts = line.split(',').map(p => p.trim());
      if (parts.length !== 7) {
        skippedRows.push({ line: idx + 2, reason: `Expected 7 fields, got ${parts.length}` });
        return;
      }
      const [pvVolt, pvCur, batVoltage, batCurrent, batKWh, , unixTime] = parts;
      parsedRows.push({
        PvVolt: safeParseFloat(pvVolt),
        PvCur: safeParseFloat(pvCur),
        BatVoltage: safeParseFloat(batVoltage),
        BatCurrent: safeParseFloat(batCurrent),
        LoadVoltage: safeParseFloat(batVoltage),
        LoadCurrent: safeParseFloat(batCurrent),
        BatKWh: safeParseFloat(batKWh),
        PVKWh: safeParseFloat(batKWh),
        Temperature: 0,
        RecordTime: unixToISO(unixTime),
      });
    });

    if (skippedRows.length > 0) {
      console.warn(`Skipped ${skippedRows.length} malformed row(s):`, skippedRows);
    }
    return parsedRows;
  };

  const parseStandardCSV = async (file) => {
    const results = await new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: header => header.trim(),
        complete: resolve,
        error: reject,
      });
    });

    if (results.errors.length > 0) {
      const firstError = results.errors[0];
      throw new Error(`CSV parsing error: ${firstError.message} (row ${firstError.row ?? 'unknown'})`);
    }

    return results.data
      .filter(row => row['Date & Time'] && row['PV Voltage'])
      .map(row => {
        let recordTime;
        try {
          const dateString = row['Date & Time'].trim();
          if (/^\d{2}:\d{2}:\d{2} \d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
            const [timePart, datePart] = dateString.split(' ');
            const [hours, minutes, seconds] = timePart.split(':');
            const [day, month, year] = datePart.split('/');
            recordTime = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds)).toISOString();
          } else {
            recordTime = new Date().toISOString();
          }
        } catch {
          recordTime = new Date().toISOString();
        }

        return {
          PvVolt: safeParseFloat(row['PV Voltage']),
          PvCur: safeParseFloat(row['PV Current']),
          BatVoltage: safeParseFloat(row['Bat Voltage']),
          BatCurrent: safeParseFloat(row['Bat Current']),
          LoadVoltage: safeParseFloat(row['Bat Voltage']),
          LoadCurrent: safeParseFloat(row['Bat Current']),
          BatKWh: 0,
          PVKWh: safeParseFloat(row['KwH (till date)']),
          Temperature: 0,
          RecordTime: recordTime,
        };
      });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) { toast.error("Please select a CSV file to upload."); return; }

    e.target.value = null;
    setFilename(file.name);

    if (file.type !== "text/csv" && !file.name.endsWith('.csv')) {
      toast.error("Invalid file type. Please upload a CSV file.");
      return;
    }

    const extractedUID = extractUIDFromFilename(file.name);
    setFilenameUID(extractedUID);

    const matchedDevice = findDeviceByUID(extractedUID);
    if (!matchedDevice) {
      toast.error(`The filename UID (${extractedUID}) is not registered. Please register the device first.`);
      return;
    }

    const finalUID = matchedDevice.UID;
    setSelectedUID(finalUID);
    setUploading(true);

    try {
      const firstLine = await peekFirstLine(file);
      const isRaw = isRawFirmwareFormat(firstLine);
      console.log(isRaw ? "Detected raw firmware CSV format" : "Detected standard CSV format");

      const formattedData = isRaw
        ? await parseRawFirmwareCSV(file)
        : await parseStandardCSV(file);

      if (formattedData.length === 0) throw new Error("CSV has no valid data");

      dispatch(uploadSolarData({ UID: finalUID, data: formattedData }));
      toast.success(`Data uploaded successfully for ${finalUID}`);
      onClose();

    } catch (error) {
      console.error("CSV Upload Error:", error);
      toast.error(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '8px',
        width: '500px', maxWidth: '90%', maxHeight: '90vh', overflow: 'auto'
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '15px 20px', color: 'white', backgroundColor: '#0d6efd',
          borderRadius: '8px 8px 0 0'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Upload Solar Data</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', fontSize: '1.5rem',
            cursor: 'pointer', color: 'white', lineHeight: 1
          }}>
            &times;
          </button>
        </div>

        <div style={{ padding: '16px' }}>

          {/* ── Search ── */}
          <div style={{ position: 'relative', marginBottom: '12px' }}>
            <label style={{
              display: 'block', color: '#111827',
              marginBottom: '4px', fontWeight: 600, fontSize: '0.85rem'
            }}>
              Search by UID, Location, Name, Village or District
            </label>
            <input
              type="text"
              placeholder="e.g. IND.RAJ.SHA249 or Maya Devi or Bherwai..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setShowSearchResults(true); }}
              onFocus={() => setShowSearchResults(true)}
              onBlur={() => setTimeout(() => setShowSearchResults(false), 150)}
              style={{
                width: '100%', padding: '8px 10px', borderRadius: '4px',
                border: '1px solid #d1d5db', boxSizing: 'border-box',
                fontSize: '0.9rem', color: '#111827', outline: 'none'
              }}
            />

            {/* Dropdown results */}
            {showSearchResults && query !== '' && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                backgroundColor: '#ffffff', border: '1px solid #d1d5db',
                borderRadius: '4px', maxHeight: '220px', overflowY: 'auto',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)', marginTop: '2px'
              }}>
                {filteredDevices.length === 0 ? (
                  <div style={{ padding: '10px 12px', color: '#6b7280', fontSize: '0.85rem' }}>
                    No matching device found
                  </div>
                ) : (
                  filteredDevices.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => {
                        setSelectedUID(item.UID);
                        setSearchQuery(item.UID);
                        setShowSearchResults(false);
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                      style={{
                        padding: '10px 12px', cursor: 'pointer',
                        borderBottom: idx !== filteredDevices.length - 1 ? '1px solid #f3f4f6' : 'none',
                        backgroundColor: selectedUID === item.UID ? '#dbeafe' : '#ffffff',
                        transition: 'background-color 0.1s'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedUID !== item.UID) e.currentTarget.style.backgroundColor = '#f9fafb';
                      }}
                      onMouseLeave={(e) => {
                        if (selectedUID !== item.UID) e.currentTarget.style.backgroundColor = '#ffffff';
                      }}
                    >
                      {/* UID */}
                      <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1d4ed8', letterSpacing: '0.3px' }}>
                        {item.UID}
                      </div>

                      {/* Location + Village + Block */}
                      {(item.Location || item.VillageName || item.Block) && (
                        <div style={{ fontSize: '0.75rem', color: '#374151', marginTop: '3px' }}>
                          📍 {[item.Location, item.VillageName, item.Block].filter(Boolean).join(' · ')}
                        </div>
                      )}

                      {/* Beneficiary name */}
                      {item.NameOfBeneficiary && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>
                          👤 {item.NameOfBeneficiary}
                          {item.District && <span style={{ marginLeft: '6px', color: '#9ca3af' }}>({item.District})</span>}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* ── Dropdown fallback ── */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'block', color: '#111827',
              marginBottom: '4px', fontWeight: 600, fontSize: '0.85rem'
            }}>
              Or select UID from dropdown
            </label>
            <select
              style={{
                width: '100%', padding: '8px 10px', borderRadius: '4px',
                border: '1px solid #d1d5db', color: '#111827',
                fontSize: '0.85rem', backgroundColor: '#ffffff'
              }}
              value={selectedUID}
              onChange={(e) => {
                setSelectedUID(e.target.value);
                setSearchQuery(e.target.value);
              }}
            >
              <option value="">-- Select Device UID --</option>
              {deviceList.map((item, idx) => (
                <option key={idx} value={item.UID}>
                  {item.UID}{item.Location ? ` — ${item.Location}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Selected device info */}
          {selectedUID && (() => {
            const selected = deviceList.find(d => d.UID === selectedUID);
            if (!selected) return null;
            return (
              <div style={{
                backgroundColor: '#f0fdf4', border: '1px solid #86efac',
                borderRadius: '6px', padding: '10px 12px', marginBottom: '12px',
                fontSize: '0.8rem', color: '#166534'
              }}>
                <strong>✅ Selected:</strong> {selected.UID}<br />
                {selected.Location && <span>📍 {selected.Location}{selected.VillageName ? ` · ${selected.VillageName}` : ''}<br /></span>}
                {selected.NameOfBeneficiary && <span>👤 {selected.NameOfBeneficiary}</span>}
              </div>
            );
          })()}

          {/* Filename UID validation */}
          {filename && (
            <div style={{ marginBottom: '12px' }}>
              <small style={{ color: isUIDRegistered(filenameUID) ? '#15803d' : '#dc2626', fontWeight: 500 }}>
                {isUIDRegistered(filenameUID) ? '✅' : '❌'} Filename UID: {filenameUID}
                {isUIDRegistered(filenameUID) ? ' (Registered)' : ' (Not registered — register device first)'}
              </small>
            </div>
          )}

          {/* ── Upload button ── */}
          <div style={{ marginBottom: '8px' }}>
            <label htmlFor="fileUpload" style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '10px 20px', backgroundColor: uploading ? '#93c5fd' : '#0d6efd',
              color: 'white', borderRadius: '6px', cursor: uploading ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: '0.9rem', transition: 'background-color 0.2s'
            }}>
              {uploading
                ? <><i className="fas fa-spinner fa-spin" /> Uploading...</>
                : <><i className="fa-solid fa-upload" /> Upload Device Data (CSV)</>
              }
            </label>
            <input
              id="fileUpload" type="file" accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
              disabled={uploading}
            />
          </div>

          <div style={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: '1.5' }}>
            File name must match the device UID exactly:<br />
            <code style={{ backgroundColor: '#f3f4f6', padding: '1px 4px', borderRadius: '3px', color: '#374151' }}>
              IND.RAJ.SHA195.csv
            </code>
            {' '}or{' '}
            <code style={{ backgroundColor: '#f3f4f6', padding: '1px 4px', borderRadius: '3px', color: '#374151' }}>
              8706286052516671066AFF48.csv
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadDataModal;